import { useEffect, useRef, useState } from "react";
import { Building2, Check, Loader2, MapPin, Pencil, Plus, Trash2, User, X } from "lucide-react";
import {
  useAttachables,
  useAttachToPanneau,
  useCreateAttachable,
  useDeleteAttachable,
  useDetachFromPanneau,
  useRejectProposal,
  useUpdateAttachable,
  useValidateProposal,
} from "../api/queries";
import type { AttachableKind, Proposal } from "../api/types";

interface AttachedItem {
  id: string;
  name: string;
  color?: string;
  address?: string | null;
  lieu_id?: string | null;
}

interface Props {
  kind: AttachableKind;
  panneauId: string;
  organisation: string;
  attached: AttachedItem[];
  /** Layout side: chips wrap right or left of the label. */
  side?: "left" | "right";
  /** Where the popover opens relative to the trigger. Footer chips
   *  use "top" so the popover doesn't extend below the panneau edge. */
  placement?: "bottom" | "top";
  /** Pending `attachable_attach` proposals; this chip filters by
   *  `payload.kind === kind`. Default `[]`. */
  pending?: Proposal[];
}

interface KindConfig {
  label: string;          // visible label
  icon: typeof User;
  bgClass: string;        // pill background when no color column
  placeholder: string;    // new-item input placeholder
  withColor: boolean;     // personnes have a color picker
  withAddress: boolean;   // lieux have an address text
}

const CONFIG: Record<AttachableKind, KindConfig> = {
  personnes: {
    label: "Personnes",
    icon: User,
    bgClass: "bg-chip-pink",
    placeholder: "Nouvelle personne…",
    withColor: true,
    withAddress: false,
  },
  espaces: {
    label: "Espaces",
    icon: Building2,
    bgClass: "bg-chip-orange",
    placeholder: "Nouvel espace…",
    withColor: false,
    withAddress: false,
  },
  lieux: {
    label: "Lieux",
    icon: MapPin,
    bgClass: "bg-chip-green",
    placeholder: "Nouveau lieu…",
    withColor: false,
    withAddress: true,
  },
};

const COLOR_PALETTE = [
  { name: "orange", hex: "#F39200" },
  { name: "red", hex: "#E63946" },
  { name: "green", hex: "#2A9D58" },
  { name: "pink", hex: "#E91E63" },
  { name: "gray", hex: "#9b9b9b" },
];

function colorHex(name?: string): string {
  return COLOR_PALETTE.find((c) => c.name === name)?.hex ?? "#9b9b9b";
}

/**
 * Single component that handles the personnes / espaces / lieux chip
 * cluster on a panneau. Same UX as <LabelsChip /> — pill list with ×
 * detach + a "+" button opening a popover (registry list with click-
 * to-toggle-attach + inline new-item form).
 */
export function AttachableChip({
  kind,
  panneauId,
  organisation,
  attached,
  side = "left",
  placement = "bottom",
  pending = [],
}: Props) {
  const cfg = CONFIG[kind];
  const Icon = cfg.icon;
  const [open, setOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const registry = useAttachables(kind, organisation);
  const create = useCreateAttachable(kind, organisation);
  const attach = useAttachToPanneau(kind, panneauId);
  const detach = useDetachFromPanneau(kind, panneauId);
  const update = useUpdateAttachable(kind, organisation, panneauId);
  const remove = useDeleteAttachable(kind, organisation, panneauId);
  const validateProp = useValidateProposal(panneauId);
  const rejectProp = useRejectProposal(panneauId);
  const [editingId, setEditingId] = useState<string | null>(null);

  // Pending attach proposals scoped to this chip's kind.
  const pendingForKind = pending.filter(
    (p) => p.type === "attachable_attach" && p.payload.kind === kind,
  );

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        popoverRef.current && !popoverRef.current.contains(target) &&
        buttonRef.current && !buttonRef.current.contains(target)
      ) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const attachedIds = new Set(attached.map((i) => i.id));

  return (
    <div className={`relative flex flex-col gap-1`}>
      <div className={`flex flex-wrap items-center gap-1.5 max-w-[12rem] ${side === "right" ? "justify-end" : ""}`}>
        {attached.map((i) => {
          const bg = cfg.withColor ? colorHex(i.color) : undefined;
          return (
            <span
              key={i.id}
              className={`inline-flex items-center gap-1 pl-1.5 pr-0.5 py-0.5 rounded-full text-xs text-white ${bg ? "" : cfg.bgClass}`}
              style={bg ? { backgroundColor: bg } : undefined}
              title={`${cfg.label}: ${i.name}`}
            >
              <Icon className="h-2.5 w-2.5" />
              <span>{i.name}</span>
              <button
                type="button"
                onClick={() => detach.mutate(i.id)}
                disabled={detach.isPending}
                className="rounded-full hover:bg-black/20 dark:hover:bg-white/20 px-0.5"
                aria-label={`Détacher ${i.name}`}
              >
                <X className="h-2.5 w-2.5" />
              </button>
            </span>
          );
        })}
        {pendingForKind.map((p) => {
          const name = String(p.payload.name_or_id ?? "");
          const isValidating =
            validateProp.isPending && validateProp.variables === p.id;
          const isRejecting =
            rejectProp.isPending && rejectProp.variables === p.id;
          return (
            <span
              key={p.id}
              className="inline-flex items-center gap-1 pl-1.5 pr-0.5 py-0.5 rounded-full text-xs italic opacity-70 border border-dashed border-ink/40 dark:border-white/40 bg-white/60 dark:bg-surface-dark/60 text-ink dark:text-ink-dark"
              title={`Proposition d'attachement : ${name} — « ${p.source_quote} »`}
            >
              <Icon className="h-2.5 w-2.5" />
              <span className="uppercase tracking-wide text-[10px] opacity-80">
                à valider
              </span>
              <span>{name}</span>
              <button
                type="button"
                onClick={() => validateProp.mutate(p.id)}
                disabled={isValidating || isRejecting}
                className="rounded-full hover:bg-black/10 dark:hover:bg-white/10 px-0.5"
                aria-label={`Valider l'attachement ${name}`}
                title="Valider"
              >
                {isValidating ? (
                  <Loader2 className="h-2.5 w-2.5 animate-spin" />
                ) : (
                  <Check className="h-2.5 w-2.5" />
                )}
              </button>
              <button
                type="button"
                onClick={() => rejectProp.mutate(p.id)}
                disabled={isValidating || isRejecting}
                className="rounded-full hover:bg-black/10 dark:hover:bg-white/10 px-0.5"
                aria-label={`Refuser l'attachement ${name}`}
                title="Refuser"
              >
                {isRejecting ? (
                  <Loader2 className="h-2.5 w-2.5 animate-spin" />
                ) : (
                  <X className="h-2.5 w-2.5" />
                )}
              </button>
            </span>
          );
        })}
        <button
          ref={buttonRef}
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="h-5 w-5 rounded-full border border-dashed border-black/30 dark:border-white/30 text-muted dark:text-muted-dark hover:text-ink dark:hover:text-ink-dark hover:border-ink dark:hover:border-white flex items-center justify-center"
          aria-label={`Gérer les ${cfg.label.toLowerCase()}`}
        >
          <Plus className="h-3 w-3" />
        </button>
      </div>
      <span className={`text-xs text-muted dark:text-muted-dark ${side === "right" ? "text-right" : ""}`}>{cfg.label}</span>

      {open && (
        <div
          ref={popoverRef}
          className={`absolute ${side === "right" ? "right-0" : "left-0"} ${placement === "top" ? "bottom-full mb-2" : "top-full mt-2"} z-20 w-72 bg-white dark:bg-surface-dark rounded-xl border border-black/10 dark:border-white/10 shadow-lg p-2`}
          role="dialog"
          aria-label={`Gestion ${cfg.label.toLowerCase()}`}
        >
          {registry.isLoading && (
            <div className="text-sm text-muted dark:text-muted-dark px-2 py-1.5 flex items-center gap-2">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Chargement…
            </div>
          )}
          {registry.data && registry.data.length === 0 && (
            <div className="text-xs text-muted dark:text-muted-dark px-2 py-1.5">
              Aucun(e) {cfg.label.toLowerCase().slice(0, -1)} dans cette organisation. Créez-en un(e) ci-dessous.
            </div>
          )}
          <ul className="max-h-56 overflow-y-auto">
            {registry.data?.map((item) => {
              const isAttached = attachedIds.has(item.id);
              const isEditing = editingId === item.id;
              if (isEditing) {
                return (
                  <li key={item.id} className="px-1 py-1.5">
                    <EditAttachableRow
                      cfg={cfg}
                      item={item as AttachedItem}
                      pending={update.isPending}
                      onSave={async (patch) => {
                        await update.mutateAsync({ id: item.id, ...patch });
                        setEditingId(null);
                      }}
                      onCancel={() => setEditingId(null)}
                    />
                  </li>
                );
              }
              return (
                <li key={item.id} className="flex items-center gap-1 group">
                  <button
                    type="button"
                    onClick={() =>
                      isAttached ? detach.mutate(item.id) : attach.mutate(item.id)
                    }
                    disabled={attach.isPending || detach.isPending}
                    className="flex-1 text-left px-2 py-1.5 rounded hover:bg-canvas dark:hover:bg-canvas-dark flex items-center gap-2 text-sm min-w-0"
                  >
                    {cfg.withColor ? (
                      <span
                        className="h-3 w-3 rounded-full shrink-0"
                        style={{ backgroundColor: colorHex((item as { color?: string }).color) }}
                      />
                    ) : (
                      <Icon className="h-3.5 w-3.5 text-muted dark:text-muted-dark shrink-0" />
                    )}
                    <span className="flex-1 truncate">{item.name}</span>
                    {isAttached && (
                      <span className="text-ink dark:text-ink-dark text-xs" aria-label="Attaché">✓</span>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditingId(item.id)}
                    className="opacity-100 p-1 text-muted dark:text-muted-dark hover:text-ink dark:hover:text-ink-dark transition-colors"
                    aria-label={`Modifier ${item.name}`}
                    title="Modifier"
                  >
                    <Pencil className="h-3 w-3" />
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (
                        window.confirm(
                          `Supprimer « ${item.name} » du registre ? Cela retire l'élément de tous les panneaux.`,
                        )
                      ) {
                        remove.mutate(item.id);
                      }
                    }}
                    disabled={remove.isPending}
                    className="opacity-100 p-1 text-muted dark:text-muted-dark hover:text-ink dark:hover:text-ink-dark transition-colors"
                    aria-label={`Supprimer ${item.name}`}
                    title="Supprimer du registre"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </li>
              );
            })}
          </ul>
          <NewAttachableForm
            cfg={cfg}
            onCreate={async (body) => {
              const created = await create.mutateAsync(body);
              await attach.mutateAsync(created.id);
            }}
            pending={create.isPending || attach.isPending}
          />
        </div>
      )}
    </div>
  );
}

interface NewFormProps {
  cfg: KindConfig;
  onCreate: (body: Record<string, unknown>) => Promise<unknown>;
  pending: boolean;
}

function NewAttachableForm({ cfg, onCreate, pending }: NewFormProps) {
  const [name, setName] = useState("");
  const [color, setColor] = useState("orange");
  const [address, setAddress] = useState("");

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    const body: Record<string, unknown> = { name: trimmed };
    if (cfg.withColor) body.color = color;
    if (cfg.withAddress && address.trim()) body.address = address.trim();
    try {
      await onCreate(body);
      setName("");
      setAddress("");
    } catch {
      /* swallow — v1 has no toast */
    }
  };

  return (
    <form onSubmit={submit} className="border-t border-black/5 dark:border-white/10 mt-1 pt-2 px-1 space-y-1.5">
      {cfg.withColor && (
        <div className="flex items-center gap-1.5">
          {COLOR_PALETTE.map((c) => (
            <button
              key={c.name}
              type="button"
              onClick={() => setColor(c.name)}
              className={`h-7 w-7 sm:h-5 sm:w-5 rounded-full ${color === c.name ? "ring-2 ring-ink dark:ring-white ring-offset-1" : ""}`}
              style={{ backgroundColor: c.hex }}
              aria-label={`Couleur ${c.name}`}
              title={c.name}
            />
          ))}
        </div>
      )}
      <div className="flex items-center gap-1">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={cfg.placeholder}
          maxLength={120}
          className="bun-textbox flex-1 px-2 py-1 text-sm rounded border border-black/10 dark:border-white/10 focus:outline-none focus:ring-1 focus:ring-ink dark:focus:ring-white"
        />
        <button
          type="submit"
          disabled={pending || !name.trim()}
          className="h-7 w-7 rounded-full bg-ink text-white flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed"
          aria-label="Créer"
        >
          {pending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
        </button>
      </div>
      {cfg.withAddress && (
        <input
          type="text"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          placeholder="Adresse (optionnel)"
          maxLength={500}
          className="bun-textbox w-full px-2 py-1 text-sm rounded border border-black/10 dark:border-white/10 focus:outline-none focus:ring-1 focus:ring-ink dark:focus:ring-white"
        />
      )}
    </form>
  );
}

interface EditRowProps {
  cfg: KindConfig;
  item: AttachedItem;
  pending: boolean;
  onSave: (patch: Record<string, unknown>) => Promise<unknown>;
  onCancel: () => void;
}

function EditAttachableRow({ cfg, item, pending, onSave, onCancel }: EditRowProps) {
  const [name, setName] = useState(item.name);
  const [color, setColor] = useState(item.color ?? "gray");
  const [address, setAddress] = useState(item.address ?? "");

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    const patch: Record<string, unknown> = {};
    if (trimmed !== item.name) patch.name = trimmed;
    if (cfg.withColor && color !== (item.color ?? "gray")) patch.color = color;
    if (cfg.withAddress) {
      const trimmedAddress = address.trim();
      const cur = item.address ?? "";
      if (trimmedAddress !== cur) {
        patch.address = trimmedAddress === "" ? null : trimmedAddress;
      }
    }
    if (Object.keys(patch).length === 0) {
      onCancel();
      return;
    }
    try {
      await onSave(patch);
    } catch {
      /* swallowed; v1 has no toast */
    }
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-1.5 bg-canvas/50 dark:bg-canvas-dark/50 rounded p-1.5">
      {cfg.withColor && (
        <div className="flex items-center gap-1.5">
          {COLOR_PALETTE.map((c) => (
            <button
              key={c.name}
              type="button"
              onClick={() => setColor(c.name)}
              className={`h-6 w-6 sm:h-4 sm:w-4 rounded-full ${
                color === c.name ? "ring-2 ring-ink dark:ring-white ring-offset-1" : ""
              }`}
              style={{ backgroundColor: c.hex }}
              aria-label={`Couleur ${c.name}`}
            />
          ))}
        </div>
      )}
      <div className="flex items-center gap-1">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={120}
          autoFocus
          className="bun-textbox flex-1 px-2 py-1 text-sm rounded border border-black/10 dark:border-white/10 focus:outline-none focus:ring-1 focus:ring-ink dark:focus:ring-white"
        />
        <button
          type="submit"
          disabled={pending || !name.trim()}
          className="h-6 w-6 rounded-full bg-ink text-white flex items-center justify-center disabled:opacity-30"
          aria-label="Enregistrer"
        >
          {pending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={pending}
          className="h-6 w-6 rounded-full bg-white dark:bg-surface-dark border border-black/10 dark:border-white/10 text-muted dark:text-muted-dark flex items-center justify-center"
          aria-label="Annuler"
        >
          <X className="h-3 w-3" />
        </button>
      </div>
      {cfg.withAddress && (
        <input
          type="text"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          placeholder="Adresse (optionnel)"
          maxLength={500}
          className="bun-textbox w-full px-2 py-1 text-sm rounded border border-black/10 dark:border-white/10 focus:outline-none focus:ring-1 focus:ring-ink dark:focus:ring-white"
        />
      )}
    </form>
  );
}
